import React from 'react';

import LinkIcon from '@atlaskit/icon/core/link';
import Tile from '@atlaskit/tile/tile';

import type { LoadableType } from '@confluence/loadable';
import { LoadableLazy } from '@confluence/loadable';

export type ThirdPartyIconProps = {
	/** Third-party ARI, e.g. "ari:third-party:google.google-drive::document/1" */
	thirdPartyId?: string | null;
	/** Logo tile size: "xsmall" (20px, the default), e.g. in navigation, or "medium" (32px), e.g. in page cards */
	size?: 'xsmall' | 'medium';
};

/**
 * Generic icon for a third-party (3P) item, shown when there's no provider logo. At "medium" it's in a
 * bordered tile, like the logos and the content icons around it.
 */
export const ThirdPartyFallbackIcon = ({ size }: ThirdPartyIconProps): React.JSX.Element =>
	size === 'medium' ? (
		<Tile label="" size="medium" backgroundColor="transparent" hasBorder>
			<LinkIcon label="" />
		</Tile>
	) : (
		<LinkIcon label="" />
	);

/**
 * The provider's logo for a 3P item, e.g. in Recent. Logos are lazy-loaded to keep them out of
 * the main bundle; the generic icon shows while they load and for unknown providers.
 */
export const ThirdPartyIcon: LoadableType<ThirdPartyIconProps> = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ThirdPartyLogo" */ './ThirdPartyLogo.tsx'))
			.ThirdPartyLogo,
	loading: ThirdPartyFallbackIcon,
});
